import xor from 'lodash/xor'

/**
 * Toggle a value in a multi-select where an empty selection means
 * "all selected". Selecting/deselecting normalises back to an empty array
 * once every option is active again, so the filter stays in its default
 * (unfiltered) state.
 */
export const toggleMultiSelect = <T extends string>(
  value: T,
  current: readonly T[],
  allValues: readonly T[]
): T[] => {
  const base = current.length === 0 ? allValues : current
  const next = base.includes(value)
    ? base.filter(v => v !== value)
    : [...base, value]
  return next.length === allValues.length ? [] : next
}

/**
 * Categories are stored as an *exclusion* list: an empty array means every
 * category is selected. Toggling a single category flips its membership in
 * that exclusion list, collapsing back to "all selected" when the toggle
 * would otherwise exclude every category.
 */
export const toggleCategoryExclusion = (
  id: string,
  excludeCategories: readonly string[],
  allCategoryIds: readonly string[]
): string[] => {
  if (excludeCategories.length === 0) {
    // Everything is currently selected: deselect just this one.
    return allCategoryIds.filter(catId => catId !== id)
  }

  const next = xor([...excludeCategories], [id])
  // Excluding every category is meaningless — reset to "all selected".
  return next.length === allCategoryIds.length ? [] : next
}

/** A category is checked when it is not present in the exclusion list. */
export const isCategorySelected = (
  id: string,
  excludeCategories: readonly string[]
): boolean => excludeCategories.length === 0 || !excludeCategories.includes(id)
